import { collection, limit, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestoreDb } from './firebase'
import { subscribeWorkspaceUserDirectory } from './workspaceUserDirectory'
import type { MeetingAgendaNode, MeetingAttachment, MeetingCreateInput, MeetingProvider, MeetingRecord, MeetingRsvpStatus, MeetingStatus } from '../types/meetings'
import { visibilityAwareInterval } from '../utils/visibilityAwareInterval'
import { firestoreTimeToMillis } from '../utils/firestoreTime'

const callable = <Input, Output>(name: string) => httpsCallable<Input, Output>(firebaseFunctions, name)
const ALLOWED_JOIN_HOSTS: Record<Exclude<MeetingProvider, 'HUDDLE'>, string[]> = { ZOOM: ['zoom.us', 'www.zoom.us'], WEBEX: ['web.webex.com'], GOOGLE_MEET: ['meet.google.com'] }

export type MeetingMember = { uid: string; name: string; role: string }
export type MeetingBusySlot = { start: string; end: string; source: 'MEETING' | 'GOOGLE_CALENDAR' }
export type MeetingDetailsUpdateInput = {
  meetingId: string
  title: string
  description?: string
  scheduledAt: string
  durationMinutes: number
  participantIds: string[]
  /**
   * How to notify participants after this save:
   *  - 'ALL'     — notify every current participant that the meeting changed.
   *  - 'CHANGED' — notify only newly-added participants.
   *  - 'NONE'    — silent save. Server rejects this when the change adds
   *                new participants (they would never learn about the meeting).
   *                Allowed for pure field edits, or edits that only remove
   *                participants.
   */
  notifyMode: 'ALL' | 'CHANGED' | 'NONE'
  chatChannelIds?: string[]
  scopeNodeId?: string | null
}

export const meetingStatusLabel = (status: MeetingStatus) => {
  if (status === 'LIVE') return 'In Progress'
  if (status === 'COMPLETED') return 'Concluded'
  if (status === 'SCHEDULED') return 'Scheduled'
  if (status === 'READY') return 'Ready'
  if (status === 'CONNECTION_INTERRUPTED') return 'Connection Interrupted'
  return status === 'CANCELLED' ? 'Cancelled' : 'Draft'
}

export interface MeetingProviderAdapter {
  createMeeting(input: MeetingCreateInput): Promise<{ meetingId: string }>
  joinMeeting(meetingId: string): Promise<{ joinUrl: string }>
  syncStatus(meetingId: string): Promise<{ status: MeetingStatus }>
}

const validateJoinUrl = (sourceProvider: MeetingProvider, raw: string) => {
  if (sourceProvider === 'HUDDLE') return raw
  const url = new URL(raw)
  if (url.protocol !== 'https:' || !ALLOWED_JOIN_HOSTS[sourceProvider].includes(url.hostname.toLowerCase())) throw new Error('Provider returned an unsafe meeting URL.')
  return url.toString()
}

export const createMeetingProviderAdapter = (sourceProvider: MeetingProvider): MeetingProviderAdapter => ({
  async createMeeting(input) {
    if (input.sourceProvider !== sourceProvider) throw new Error('Meeting provider payload mismatch.')
    return (await callable<MeetingCreateInput, { meetingId: string }>('createMeeting')(input)).data
  },
  async joinMeeting(meetingId) {
    const result = (await callable<{ meetingId: string }, { joinUrl: string }>('joinMeeting')({ meetingId })).data
    return { joinUrl: validateJoinUrl(sourceProvider, result.joinUrl) }
  },
  async syncStatus(meetingId) {
    return (await callable<{ meetingId: string }, { status: MeetingStatus }>('syncMeetingStatus')({ meetingId })).data
  },
})

export const subscribeMeetings = (
  workspaceId: string,
  onChange: (meetings: MeetingRecord[]) => void,
  onError?: (error: Error) => void,
  scope?: { restricted: boolean; uid: string },
): Unsubscribe => {
  if (scope?.restricted) {
    const uid = scope.uid
    let participantMeetings: MeetingRecord[] = []
    let scopeReadMeetings: MeetingRecord[] = []

    const emitMerged = () => {
      const byId = new Map<string, MeetingRecord>()
      for (const item of participantMeetings) byId.set(item.id, item)
      for (const item of scopeReadMeetings) byId.set(item.id, item)
      const merged = Array.from(byId.values())
      merged.sort((a, b) => firestoreTimeToMillis(a.scheduledAt) - firestoreTimeToMillis(b.scheduledAt))
      onChange(merged.slice(0, 100))
    }

    const unsubParticipant = onSnapshot(
      query(
        collection(firestoreDb, 'meetings'),
        where('workspaceId', '==', workspaceId),
        where('participantUids', 'array-contains', uid),
        orderBy('scheduledAt', 'asc'),
        limit(100),
      ),
      (snapshot) => {
        participantMeetings = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as MeetingRecord)
        emitMerged()
      },
      (error) => onError?.(error),
    )

    const unsubScopeRead = onSnapshot(
      query(
        collection(firestoreDb, 'meetings'),
        where('workspaceId', '==', workspaceId),
        where('scopeRead', 'array-contains', uid),
        orderBy('scheduledAt', 'asc'),
        limit(100),
      ),
      (snapshot) => {
        scopeReadMeetings = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as MeetingRecord)
        emitMerged()
      },
      (error) => onError?.(error),
    )

    return () => {
      unsubParticipant()
      unsubScopeRead()
    }
  }

  return onSnapshot(
    query(collection(firestoreDb, 'meetings'), where('workspaceId', '==', workspaceId), orderBy('scheduledAt', 'asc'), limit(100)),
    (snapshot) => onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as MeetingRecord)),
    (error) => onError?.(error),
  )
}

export const subscribeMeetingMembers = (workspaceId: string, owner: { id: string; name: string }, personal: boolean, onChange: (members: MeetingMember[]) => void, onError?: (error: Error) => void) => {
  if (personal) { onChange([{ uid: owner.id, name: owner.name, role: 'ADMIN' }]); return () => undefined }
  return subscribeWorkspaceUserDirectory(
    workspaceId,
    (members) => onChange(members.map((member) => ({ uid: member.uid, name: member.displayName, role: member.role }))),
    onError,
  )
}

export const subscribeGoogleCalendarBusySlots = (workspaceId: string, onChange: (slots: MeetingBusySlot[]) => void) => {
  let active = true
  const load = async () => {
    try {
      const result = await callable<{ workspaceId: string }, { slots: Array<{ start: string; end: string }> }>('getGoogleCalendarBusySlots')({ workspaceId })
      if (active) onChange(result.data.slots.map((slot) => ({ ...slot, source: 'GOOGLE_CALENDAR' as const })))
    } catch { if (active) onChange([]) }
  }
  void load()
  const stopTimer = visibilityAwareInterval(() => { void load() }, 60_000)
  return () => { active = false; stopTimer() }
}

export const updateMeetingAgenda = async (meetingId: string, agendaMarkdown: string, agendaNodes: MeetingAgendaNode[]) => (await callable<{ meetingId: string; agendaMarkdown: string; agendaNodes: MeetingAgendaNode[] }, { updated: true }>('updateMeetingAgenda')({ meetingId, agendaMarkdown, agendaNodes })).data
export const updateMeetingAttachments = async (meetingId: string, attachments: MeetingAttachment[]) => (await callable<{ meetingId: string; attachments: MeetingAttachment[] }, { updated: true }>('updateMeetingAttachments')({ meetingId, attachments })).data
export const updateMeetingDetails = async (input: MeetingDetailsUpdateInput) => (await callable<MeetingDetailsUpdateInput, { updated: true; addedParticipantIds: string[]; removedParticipantIds: string[]; notifiedParticipantIds: string[]; notificationsSent: boolean }>('updateMeetingDetails')(input)).data
export const updateMeetingRsvp = async (meetingId: string, status: MeetingRsvpStatus) => (await callable<{ meetingId: string; status: MeetingRsvpStatus }, { updated: true }>('updateMeetingRsvp')({ meetingId, status })).data
export const cancelMeeting = async (meetingId: string) => (await callable<{ meetingId: string }, { cancelled: true }>('cancelMeeting')({ meetingId })).data
export const deleteMeeting = async (meetingId: string) => (await callable<{ meetingId: string }, { deleted: true; gcalStatus: string }>('deleteMeeting')({ meetingId })).data
export const resyncMeetingCalendar = async (meetingId: string) => (await callable<{ meetingId: string }, { status: string; gcalEventId: string | null }>('resyncMeetingCalendar')({ meetingId })).data
export const bulkPushMeetingsToGoogleCalendar = async (input: { workspaceId: string; startAt: string; endAt: string }) => (await callable<typeof input, { attempted: number; synced: number; failed: number }>('bulkPushMeetingsToGoogleCalendar')(input)).data
export const requestMeetingTranscription = async (meetingId: string) => (await callable<{ meetingId: string }, { jobId: string; status: 'STUB_QUEUED'; creditsCharged: number }>('requestMeetingTranscription')({ meetingId })).data

export type ReadinessNode = { id: string; nodeType: 'ELEMENT' | 'CHECKLIST'; completed: boolean; weight: number }
export const calculateMeetingReadiness = (agendaNodes: MeetingAgendaNode[], nodes: ReadinessNode[]) => {
  if (!agendaNodes.length) return { score: 0, completed: 0, total: 0 }
  const lookup = new Map(nodes.map((node) => [`${node.nodeType}:${node.id}`, node]))
  let completedWeight = 0; let totalWeight = 0; let completed = 0
  for (const agendaNode of agendaNodes) {
    const node = lookup.get(`${agendaNode.nodeType}:${agendaNode.nodeId}`); const weight = Math.max(1, node?.weight ?? 1)
    totalWeight += weight
    if (node?.completed) { completedWeight += weight; completed += 1 }
  }
  return { score: totalWeight ? Math.round(completedWeight / totalWeight * 100) : 0, completed, total: agendaNodes.length }
}
